import { AxiosResponse } from "axios";
import apiClient from "../api/axios";
import { handleApiError } from "../api/handleApiError";
import { Assets, AssetsType, AssetType, AssetTypeResponse, isPickableAsset } from "../models/Asset";
import { StructureDeletePreview } from "../models/StructureDelete";
import i18n, { organization } from "../i18n";

interface AssetsResponse {
  results: { assets: Assets[] };
}

const AssetsService = {
  getAll: async (): Promise<Assets[]> => {
    try {
      const response: AxiosResponse<AssetsResponse> = await apiClient.get("/v1/assets");
      return response.data.results.assets.filter(isPickableAsset);
    } catch (error) {
      handleApiError(error, {}, "error-get-assets");
    }
    return [];
  },

  getDeletePreview: async (refId: string): Promise<StructureDeletePreview | null> => {
    try {
      const response = await apiClient.get(`/v2/assets/${refId}/delete-preview`);
      if (response.status === 200) {
        return response.data.results;
      }
    } catch (error) {
      handleDeleteError(error);
    }
    return null;
  },

  // keep (default) leaves the records as they are; deleteRecords sends the preview's fingerprint
  deleteAsset: async (refId: string, deleteRecords: boolean, fingerprint: string): Promise<boolean> => {
    try {
      const response = await apiClient.delete(`/v2/assets/${refId}`, {
        params: deleteRecords ? { deleteRecords: true, fingerprint } : {},
        _own503Codes: ["EVIDENCE_DELETE_PROCESS_FAILED"],
      } as any);
      return response.status === 200;
    } catch (error) {
      handleDeleteError(error);
    }
    return false;
  },

  getAssetTypes: async (emissionGroup: AssetsType): Promise<AssetType[]> => {
    try {
      const response: AxiosResponse<AssetTypeResponse> = await apiClient.get(
        `/v1/asset-types/${emissionGroup}`
      );
      return response.data.results.assetTypes;
    } catch (error) {
      handleApiError(error);
    }
    return [];
  },
};

// the alert translates from common and input only, so these texts arrive translated
export const organizationText = (key: string) => () => i18n.t(key, { ns: organization });

const handleDeleteError = (error: unknown) =>
  handleApiError(
    error,
    {
      DELETE_PREVIEW_OUTDATED: { messageKey: organizationText("Structure-Delete-Outdated"), type: "info" },
      // a kept asset's only action is its records delete, so the asset text names it
      EVIDENCE_DELETE_PROCESS_FAILED: {
        messageKey: organizationText("Structure-Delete-Files-Retry-Asset"),
        type: "info",
      },
    },
    { messageKey: organizationText("error-delete-assets"), titleKey: "Error-Message" },
  );

export default AssetsService;
